import { SessionStorageKeys } from '@invision/core';
import {
  STORAGE_KEYS,
  AUTH_STORAGE_KEYS,
  OIDC_CLIENT_STORAGE_KEY_PREFIX,
  LEGACY_OIDC_CLIENT_STORAGE_KEY_PREFIX
} from '../constants';
import { SimpleStorageService } from './SimpleStorageService';
import type { IdpAuthState } from '../services/idpRedirectService';
import type { EnvironmentValue } from '../constants';
import type { OAuthSettings } from '../types';
import { AuthLogger } from './logger';

const OIDC_CLIENT_STORAGE_KEY_PREFIXES = [
  OIDC_CLIENT_STORAGE_KEY_PREFIX,
  LEGACY_OIDC_CLIENT_STORAGE_KEY_PREFIX
] as const;

/**
 * Authentication Storage Class
 * Manages authentication-related data persistence using SimpleStorageService
 * Provides straightforward methods for session and local storage management
 */
class AuthenticationStorageClass {
  private sessionStorage: SimpleStorageService;

  private localStorage: SimpleStorageService;

  constructor() {
    this.sessionStorage = new SimpleStorageService({
      useSessionStorage: true
    });
    this.localStorage = new SimpleStorageService({
      useSessionStorage: false
    });
  }

  private emitStorageChange(key: string): void {
    // Emit custom event for same-window storage changes
    // This enables automatic re-renders for components using storage hooks
    try {
      window.dispatchEvent(
        new CustomEvent('auth-storage-change', {
          detail: { key }
        })
      );
    } catch (error) {
      AuthLogger.warn('Failed to emit storage change event:', error);
    }
  }

  // Session ID management
  getSessionId(): string | null {
    return this.sessionStorage.retrieve(STORAGE_KEYS.SESSION_ID);
  }

  setSessionId(sessionId: string): void {
    this.sessionStorage.save(STORAGE_KEYS.SESSION_ID, sessionId);
    this.emitStorageChange(STORAGE_KEYS.SESSION_ID);
  }

  removeSessionId(): void {
    this.sessionStorage.remove(STORAGE_KEYS.SESSION_ID);
    this.emitStorageChange(STORAGE_KEYS.SESSION_ID);
  }

  // System ID management
  getSystemId(): string | null {
    return this.sessionStorage.retrieve(STORAGE_KEYS.SYSTEM_ID);
  }

  setSystemId(systemId: string): void {
    this.sessionStorage.save(STORAGE_KEYS.SYSTEM_ID, systemId);
    this.emitStorageChange(STORAGE_KEYS.SYSTEM_ID);
  }

  removeSystemId(): void {
    this.sessionStorage.remove(STORAGE_KEYS.SYSTEM_ID);
    this.emitStorageChange(STORAGE_KEYS.SYSTEM_ID);
  }

  // Redirect URL management
  getRedirectUrl(): string | null {
    return this.sessionStorage.retrieve(STORAGE_KEYS.AUTHENTICATION_REDIRECT);
  }

  setRedirectUrl(url: string): void {
    this.sessionStorage.save(STORAGE_KEYS.AUTHENTICATION_REDIRECT, url);
    this.emitStorageChange(STORAGE_KEYS.AUTHENTICATION_REDIRECT);
  }

  removeRedirectUrl(): void {
    this.sessionStorage.remove(STORAGE_KEYS.AUTHENTICATION_REDIRECT);
    this.emitStorageChange(STORAGE_KEYS.AUTHENTICATION_REDIRECT);
  }

  // Last username for UX convenience
  getLastUsername(): string | null {
    return this.localStorage.retrieve(AUTH_STORAGE_KEYS.LAST_USERNAME);
  }

  setLastUsername(username: string): void {
    this.localStorage.save(AUTH_STORAGE_KEYS.LAST_USERNAME, username);
    this.emitStorageChange(AUTH_STORAGE_KEYS.LAST_USERNAME);
  }

  removeLastUsername(): void {
    this.localStorage.remove(AUTH_STORAGE_KEYS.LAST_USERNAME);
    this.emitStorageChange(AUTH_STORAGE_KEYS.LAST_USERNAME);
  }

  // Selected environment for development convenience
  getSelectedEnvironment(): EnvironmentValue | null {
    // Environment is stored as object, retrieve and parse it
    const environmentString = this.sessionStorage.retrieve(
      STORAGE_KEYS.ENVIRONMENT
    );
    if (!environmentString) return null;

    try {
      const environmentObject = JSON.parse(environmentString);
      return environmentObject?.value || null;
    } catch {
      // Fallback for simple string format
      return environmentString as EnvironmentValue;
    }
  }

  setSelectedEnvironment(environment: EnvironmentValue): void {
    // Store environment as object
    const environmentObject = { name: environment, value: environment };
    this.sessionStorage.save(STORAGE_KEYS.ENVIRONMENT, environmentObject);
    this.emitStorageChange(STORAGE_KEYS.ENVIRONMENT);
  }

  removeSelectedEnvironment(): void {
    this.sessionStorage.remove(STORAGE_KEYS.ENVIRONMENT);
    this.emitStorageChange(STORAGE_KEYS.ENVIRONMENT);
  }

  /**
   * Records that this tab authenticated through the React auth app so logout,
   * idle timeout, and session expiry stay on React auth in environments that
   * are not React-auth-enabled via config. Written only after a confirmed
   * successful login (password or IDP/OAuth).
   *
   * TODO(AB#1355453): Remove with the auth-mode opt-in once React auth is the
   * default in every environment.
   */
  setReactAuthMode(): void {
    this.sessionStorage.save(
      STORAGE_KEYS.AUTH_MODE,
      SessionStorageKeys.AUTH_MODE_REACT
    );
    this.emitStorageChange(STORAGE_KEYS.AUTH_MODE);
  }

  /** TODO(AB#1355453): Remove with the auth-mode opt-in once React auth is default. */
  removeAuthMode(): void {
    this.sessionStorage.remove(STORAGE_KEYS.AUTH_MODE);
    this.emitStorageChange(STORAGE_KEYS.AUTH_MODE);
  }

  // Clear all authentication data (but preserve username for UX)
  clearAll(): void {
    this.removeSessionId();
    this.removeSystemId();
    this.removeRedirectUrl();
    this.clearOAuthFlow();
    // Keep lastUsername, selectedEnvironment, and auth mode for UX / re-login
  }

  // Clear all including username and environment (for complete logout)
  clearAllIncludingUsername(): void {
    this.clearAll();
    this.removeLastUsername();
    this.removeSelectedEnvironment();
    // TODO(AB#1355453): Remove with the auth-mode opt-in once React auth is default.
    this.removeAuthMode();
  }

  // External identity provider state management
  getIdpState(): IdpAuthState | null {
    const stateString = this.sessionStorage.retrieve(
      AUTH_STORAGE_KEYS.IDP_STATE
    );
    if (!stateString) return null;

    try {
      return JSON.parse(stateString) as IdpAuthState;
    } catch {
      return null;
    }
  }

  setIdpState(state: IdpAuthState): void {
    this.sessionStorage.save(
      AUTH_STORAGE_KEYS.IDP_STATE,
      JSON.stringify(state)
    );
    this.emitStorageChange(AUTH_STORAGE_KEYS.IDP_STATE);
  }

  removeIdpState(): void {
    this.sessionStorage.remove(AUTH_STORAGE_KEYS.IDP_STATE);
    this.emitStorageChange(AUTH_STORAGE_KEYS.IDP_STATE);
  }

  /**
   * OAuth provider settings for an in-flight sign in, written by the login page
   * and read by the OAuth callback page. Held in session storage: the flow
   * lasts one round trip to the external provider within a single tab.
   */
  getOAuthSettings(): OAuthSettings | null {
    const settingsString = this.sessionStorage.retrieve(
      AUTH_STORAGE_KEYS.OAUTH_SETTINGS
    );
    if (!settingsString) return null;

    try {
      return JSON.parse(settingsString) as OAuthSettings;
    } catch (error) {
      AuthLogger.warn('Failed to parse stored OAuth settings:', error);
      return null;
    }
  }

  setOAuthSettings(settings: OAuthSettings): void {
    this.sessionStorage.save(AUTH_STORAGE_KEYS.OAUTH_SETTINGS, settings);
    this.emitStorageChange(AUTH_STORAGE_KEYS.OAUTH_SETTINGS);
  }

  /**
   * Removes the settings while the oidc-client-ts request state and PKCE
   * verifier stay in place. Called mid flow, once the callback page has read
   * the settings and before it exchanges the authorization code.
   */
  removeOAuthSettings(): void {
    this.sessionStorage.remove(AUTH_STORAGE_KEYS.OAUTH_SETTINGS);
    this.emitStorageChange(AUTH_STORAGE_KEYS.OAUTH_SETTINGS);
  }

  /**
   * Removes the OAuth provider settings and the cached provider user and
   * request state owned by oidc-client-ts. Also sweeps leftovers from before
   * the callback pinned session storage with an explicit prefix: settings in
   * local storage, and `oidc.` entries in either store. Runs on logout and
   * when an OAuth sign in is abandoned from the callback error screen, so a
   * cached provider token cannot restore a session on the next sign in.
   */
  clearOAuthFlow(): void {
    this.removeOAuthSettings();
    // Pre-session-scoped flow wrote settings here; remove so they cannot linger.
    this.localStorage.remove(AUTH_STORAGE_KEYS.OAUTH_SETTINGS);

    this.removeOidcClientEntries(this.sessionStorage);
    this.removeOidcClientEntries(this.localStorage);
  }

  private removeOidcClientEntries(storage: SimpleStorageService): void {
    storage
      .getAllKeys()
      .filter((key) =>
        OIDC_CLIENT_STORAGE_KEY_PREFIXES.some((prefix) =>
          key.startsWith(prefix)
        )
      )
      .forEach((key) => storage.remove(key));
  }

  // Check if user is authenticated (has valid session)
  isAuthenticated(): boolean {
    return !!(this.getSessionId() && this.getSystemId());
  }
}
// Export singleton instance for consistent usage across the application
export const AuthenticationStorage = new AuthenticationStorageClass();
